import {
  type Album,
  type AlbumPhoto,
  type AlbumUploadError,
  type ViewContext,
  shortDate,
  todayIso,
  useAlbumActions,
  useAlbumPhotos,
  useAlbums,
  useClasses,
  useCourses,
  useCurrentContext,
  useWithoutPhotoConsent,
} from '@shkola/api';
import { CameraPlusIcon } from '@phosphor-icons/react/CameraPlus';
import { TrashIcon } from '@phosphor-icons/react/Trash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ErrorText, Field, Modal, Select } from '../components/ui';
import {
  Block,
  GRAD,
  Head,
  INK,
  INK_SOFT,
  PAPER,
  PageHead,
  PillIcon,
  grad,
  pillClass,
  ringButton,
} from '../design/forms';
import { ArrowUpRightIcon, CheckCircleIcon, PlusIcon, WarningCircleIcon } from '../design/icons';
import { LifeArt } from '../student/LifeArt';

/*
 * «Фотоальбом класу» in «Форми 2» (28.10): the title on the paper with who sees the album under it; the
 * albums as white cards — the cover big, the name and the day, ↗ into the album; an album is one white
 * block: for the teacher first the place to add photos (with whose parents did not agree), then the
 * photos as soft squares; a photo opens big.
 */

const KNOWN_ERRORS: ReadonlyArray<AlbumUploadError> = ['not_allowed', 'too_large', 'unsupported'];

/** Phones take 12-megapixel photos: the album needs 2048 px at most (and the right way up). */
async function prepare(file: File): Promise<Blob> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  const scale = Math.min(1, 2048 / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('unsupported'))), 'image/jpeg', 0.85),
  );
}

/** The teacher's place to add photos: the button, who must not be on them, the rules. */
function Upload({ ctx, album }: { ctx: ViewContext; album: Album }) {
  const { t } = useTranslation();
  const actions = useAlbumActions(ctx.schoolId, ctx.memberId);
  const missing = useWithoutPhotoConsent(album.classId);
  const [progress, setProgress] = useState<{ done: number; total: number }>();
  const [error, setError] = useState<string>();
  const without = missing.data ?? [];
  return (
    <div
      className="mb-5 flex flex-col gap-3 rounded-[22px] p-4 md:p-5"
      style={{ backgroundColor: PAPER }}
    >
      <div className="flex flex-wrap items-center gap-3">
        <label
          className={`${pillClass('dark')} cursor-pointer ${progress ? 'pointer-events-none opacity-50' : ''}`}
        >
          {t('album.upload')}
          <PillIcon icon={CameraPlusIcon} />
          <input
            type="file"
            accept="image/jpeg,image/png,image/heic,image/*"
            multiple
            className="hidden"
            disabled={progress !== undefined}
            onChange={async (e) => {
              const files = [...(e.target.files ?? [])];
              e.target.value = '';
              setError(undefined);
              for (const [i, file] of files.entries()) {
                setProgress({ done: i, total: files.length });
                try {
                  await actions.upload.mutateAsync({
                    albumId: album.id,
                    photo: await prepare(file),
                  });
                } catch (err) {
                  const code = (err as Error).message as AlbumUploadError;
                  setError(
                    t(`album.errors.${KNOWN_ERRORS.includes(code) ? code : 'server_error'}`),
                  );
                }
              }
              setProgress(undefined);
            }}
          />
        </label>
        {progress && (
          <span className="text-[14px]" style={{ color: INK_SOFT }}>
            {t('album.uploading', { done: progress.done + 1, total: progress.total })}
          </span>
        )}
      </div>
      <ErrorText>{error}</ErrorText>
      {without.length > 0 ? (
        <p
          className="flex items-start gap-2.5 rounded-[16px] p-3.5 text-[14px] leading-5"
          style={{ backgroundImage: grad('coral'), color: GRAD.coral.ink }}
        >
          <WarningCircleIcon size={18} weight="fill" aria-hidden className="mt-px shrink-0" />
          <span>
            {t('album.noConsent')}{' '}
            <span className="font-medium" style={{ color: INK }}>
              {without.join(', ')}
            </span>
          </span>
        </p>
      ) : (
        missing.data && (
          <p className="flex items-center gap-2 text-[14px]" style={{ color: GRAD.mint.ink }}>
            <CheckCircleIcon size={18} weight="fill" aria-hidden className="shrink-0" />
            {t('album.allConsent')}
          </p>
        )
      )}
      <div className="text-[13px] leading-5" style={{ color: INK_SOFT }}>
        <p>{t('album.rules')}</p>
        <p>{t('album.privacy')}</p>
      </div>
    </div>
  );
}

function AlbumView({
  ctx,
  album,
  staff,
  onBack,
}: {
  ctx: ViewContext;
  album: Album;
  staff: boolean;
  onBack: () => void;
}) {
  const { t } = useTranslation();
  const photos = useAlbumPhotos(album.id);
  const actions = useAlbumActions(ctx.schoolId, ctx.memberId);
  const [open, setOpen] = useState<AlbumPhoto>();
  const list = photos.data ?? [];
  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <button type="button" onClick={onBack} className={`${pillClass('light', 'sm')} pr-3.5`}>
          {t('album.back')}
        </button>
        {staff && (
          <button
            type="button"
            onClick={() => actions.removeAlbum.mutate(album.id, { onSuccess: onBack })}
            className={pillClass('light', 'sm')}
          >
            {t('album.removeAlbum')}
            <PillIcon icon={TrashIcon} size="sm" />
          </button>
        )}
      </div>
      <Block>
        <Head
          title={album.title}
          note={[
            album.takenOn && shortDate(album.takenOn),
            photos.data && t('album.photos', { count: list.length }),
          ]
            .filter(Boolean)
            .join(' · ')}
        />
        {staff && <Upload ctx={ctx} album={album} />}
        {photos.isPending ? (
          <div
            className="h-[240px] animate-pulse rounded-[22px]"
            style={{ backgroundColor: PAPER }}
            aria-busy
          />
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center">
            <LifeArt kind="photo" tone="sky" size={88} />
            <p className="mt-3 font-display text-[24px] font-light" style={{ color: INK }}>
              {t('album.noPhotos')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {list.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => setOpen(p)}
                className="group aspect-square overflow-hidden rounded-[18px]"
                style={{ backgroundColor: PAPER }}
              >
                {p.url && (
                  <img
                    src={p.url}
                    alt={p.caption ?? ''}
                    loading="lazy"
                    className="size-full object-cover transition-transform duration-500 ease-spring group-hover:scale-[1.03]"
                  />
                )}
              </button>
            ))}
          </div>
        )}
      </Block>
      {open && (
        <Modal title={open.caption ?? album.title} onClose={() => setOpen(undefined)}>
          {open.url && (
            <img src={open.url} alt={open.caption ?? ''} className="w-full rounded-[18px]" />
          )}
          {staff && (
            <div className="mt-4">
              <button
                type="button"
                onClick={() =>
                  actions.removePhoto.mutate(open, { onSuccess: () => setOpen(undefined) })
                }
                className={pillClass('light', 'sm')}
              >
                {t('album.remove')}
                <PillIcon icon={TrashIcon} size="sm" />
              </button>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

function NewAlbum({
  ctx,
  classId,
  onClose,
}: {
  ctx: ViewContext;
  classId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const actions = useAlbumActions(ctx.schoolId, ctx.memberId);
  const [title, setTitle] = useState('');
  const [takenOn, setTakenOn] = useState(todayIso());
  return (
    <Modal title={t('album.new')} onClose={onClose}>
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          actions.create.mutate(
            { classId, title, takenOn: takenOn || null },
            { onSuccess: onClose },
          );
        }}
      >
        <Field
          id="album-title"
          label={t('album.titleLabel')}
          required
          maxLength={200}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
        <Field
          id="album-date"
          type="date"
          label={t('album.takenOn')}
          value={takenOn}
          onChange={(e) => setTakenOn(e.target.value)}
          className="max-w-[220px]"
        />
        <Button type="submit" className="mt-1" disabled={!title.trim() || actions.create.isPending}>
          {t('album.create')}
        </Button>
      </form>
    </Modal>
  );
}

export function AlbumPage() {
  const { t } = useTranslation();
  const ctx = useCurrentContext();
  const family = ctx.kind === 'student' || ctx.kind === 'guardian';
  const staff = !family;
  const allClasses = useClasses(ctx.schoolId);
  const courses = useCourses({ teacherMemberId: ctx.kind === 'teacher' ? ctx.memberId : '' });
  const taught = new Set([
    ...(courses.data ?? []).map((c) => c.classId),
    ...(ctx.kind === 'teacher' && ctx.homeroomClass ? [ctx.homeroomClass.id] : []),
  ]);
  const classes = (allClasses.data ?? []).filter((c) => ctx.kind === 'admin' || taught.has(c.id));
  const [chosen, setChosen] = useState('');
  const classId = family
    ? (ctx.child.class?.id ?? '')
    : chosen ||
      (ctx.kind === 'teacher' ? ctx.homeroomClass?.id : undefined) ||
      classes[0]?.id ||
      '';
  const albums = useAlbums(classId);
  const [openId, setOpenId] = useState<string>();
  const [creating, setCreating] = useState(false);
  const list = albums.data ?? [];
  const open = list.find((a) => a.id === openId);

  return (
    <div className="flex flex-col gap-4">
      <PageHead title={t('album.title')} sub={t('album.onlyClass')}>
        {staff && (
          <>
            <Select
              aria-label={t('achievements.class')}
              value={classId}
              onChange={(e) => {
                setChosen(e.target.value);
                setOpenId(undefined);
              }}
              className="h-11"
            >
              {classes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </Select>
            {classId && (
              <button type="button" onClick={() => setCreating(true)} className={pillClass('dark')}>
                {t('album.new')}
                <PillIcon icon={PlusIcon} />
              </button>
            )}
          </>
        )}
      </PageHead>
      {open ? (
        <AlbumView ctx={ctx} album={open} staff={staff} onBack={() => setOpenId(undefined)} />
      ) : albums.isPending ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-busy>
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-[300px] animate-pulse rounded-[28px] bg-white" />
          ))}
        </div>
      ) : list.length === 0 ? (
        <Block>
          <div className="flex flex-col items-center py-12 text-center">
            <LifeArt kind="photo" tone="sky" size={96} />
            <p className="mt-3 font-display text-[26px] font-light" style={{ color: INK }}>
              {t('album.empty')}
            </p>
          </div>
        </Block>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {list.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setOpenId(a.id)}
              className="group flex min-w-0 flex-col rounded-[28px] bg-white p-2 text-left transition-transform duration-300 ease-spring hover:-translate-y-0.5"
            >
              <span
                className="flex aspect-[4/3] items-center justify-center overflow-hidden rounded-[22px]"
                style={{ backgroundColor: PAPER }}
              >
                {a.cover ? (
                  <img src={a.cover} alt="" className="size-full object-cover" />
                ) : (
                  <LifeArt kind="photo" tone="stone" size={72} />
                )}
              </span>
              <span className="flex items-center gap-3 px-3 pt-3.5 pb-2.5">
                <span className="min-w-0 flex-1">
                  <span
                    className="block truncate text-[16px] leading-[22px] font-medium"
                    style={{ color: INK }}
                  >
                    {a.title}
                  </span>
                  <span className="block text-[13px]" style={{ color: INK_SOFT }}>
                    {a.takenOn ? `${shortDate(a.takenOn)} · ` : ''}
                    {t('album.photos', { count: a.photos })}
                  </span>
                </span>
                <span
                  className={`${ringButton} group-hover:bg-[var(--sh-dark)] group-hover:text-white`}
                >
                  <ArrowUpRightIcon size={16} weight="bold" aria-hidden />
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
      {creating && classId && (
        <NewAlbum ctx={ctx} classId={classId} onClose={() => setCreating(false)} />
      )}
    </div>
  );
}
